import { isVorgangLockExpired } from '@core/domain';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useVorgaenge,
  useExportMainVorgaengeCsv,
  useBulkDeleteVorgaenge,
  useBulkRetryVorgaenge,
  useBulkZurueckstellenVorgaenge,
} from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { ToniTaube } from '@/components/animation/LadeTaubeToni';
import { FilterSummary } from '@/components/filters/FilterSummary';
import type { VorgaengeAlertPresetLocationState } from '@/components/layout/AlertBellNotification';
import { PaginationControls } from '@/components/ui/PaginationControls';
import { MAIN_STATUS_FILTER_OPTIONS } from '@/constants/status';
import { useAuth } from '@/features/auth/AuthProvider';
import { useVorgangColumnSettings } from '@/hooks/useVorgangColumnSettings';
import { useMainVorgangFilters } from '@/hooks/useVorgangFilters';
import { cn } from '@/lib/utils';
import { getColumns } from '@/pages/Vorgaenge/columns/getColumns';
import { BulkSelectionActions } from '@/pages/Vorgaenge/components/Actions/BulkSelectionActions';
import { BulkRetryConfirmationDialog } from '@/pages/Vorgaenge/components/BulkRetryConfirmationDialog';
import { ExportDropDownActions } from '@/pages/Vorgaenge/components/ExportDropDown/ExportDropDownActions';
import { SearchBar } from '@/pages/Vorgaenge/components/TableHeader/FullTextSearch';
import { getSortIcon } from '@/pages/Vorgaenge/components/TableHeader/SortIcon';
import {
  TableSettingsButton,
  TableSettingsModal,
} from '@/pages/Vorgaenge/components/TableSettingsModal';
import { COLUMN_FIELD_MAPPING } from '@/pages/Vorgaenge/mapping/columnFieldMapping';
import { DeleteDialog } from '@/pages/VorgangBearbeitung/components/DeleteDialog';
import { ZurueckstellenDialog } from '@/pages/VorgangBearbeitung/components/ZurueckstellenDialog';
import type { VorgangListeItem } from '@/types';
import type { SearchOperator, VorgangFilters } from '@/types/filters';
import { BulkOperationProgressModal } from './components/BulkOperationProgressModal';

export const VorgangTableClassNames = {
  shell:
    'flex-1 min-h-0 min-w-0 overflow-hidden rounded-lg border border-table-border bg-white',
  scrollContainer:
    'h-full min-h-0 overflow-x-auto overflow-y-auto [scrollbar-gutter:stable]',
  header:
    'sticky top-0 z-20 bg-table-header border-b border-table-border border-l-4 border-l-table-header',
  headerCell: 'text-left p-3 font-medium text-text-primary bg-table-header',
} as const;

export const VorgangTableScrollContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div className={VorgangTableClassNames.scrollContainer}>{children}</div>
);

interface VorgangTableProps {
  onEdit?: (item: VorgangListeItem) => void;
  onStateChange?: (state: {
    filters: ReturnType<typeof useMainVorgangFilters>['filters'];
    pagination: PaginationState;
    sorting: SortingState;
  }) => void;
  forcedFilters?: Partial<VorgangFilters>;
  showBulkActions?: boolean;
  selectedIds?: Set<string>;
  onSelectedIdsChange?: (next: Set<string>) => void;
}

export const VorgangTable: React.FC<VorgangTableProps> = ({
  onEdit,
  onStateChange,
  forcedFilters,
  showBulkActions = true,
  selectedIds: controlledSelectedIds,
  onSelectedIdsChange,
}) => {
  const {
    sorting,
    setSorting,
    columnOrder,
    setColumnOrder,
    fullColumnOrder,
    columnVisibility,
    setColumnVisibility,
    resetToDefault: resetColumnSettings,
  } = useVorgangColumnSettings();
  const [columnSettingsOpen, setColumnSettingsOpen] = React.useState(false);
  const [rowSelection, setRowSelection] = React.useState({});
  // Persistent selection by ID (survives pagination)
  const [internalSelectedIds, setInternalSelectedIds] = React.useState<
    Set<string>
  >(() => new Set());
  const isSelectionControlled = controlledSelectedIds !== undefined;
  const selectedIds = isSelectionControlled
    ? controlledSelectedIds
    : internalSelectedIds;
  const setSelectedIds = React.useCallback(
    (next: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      if (isSelectionControlled) {
        const value =
          typeof next === 'function' ? next(controlledSelectedIds) : next;
        onSelectedIdsChange?.(value);
      } else {
        setInternalSelectedIds(next);
      }
    },
    [isSelectionControlled, controlledSelectedIds, onSelectedIdsChange],
  );
  // Progress tracking for bulk operations
  const [progress, setProgress] = React.useState<{
    status: 'idle' | 'processing' | 'completed' | 'error';
    operation: 'delete' | 'retry' | 'shelve' | null;
    result?: {
      success: string[];
      failed: { id: string; error: string }[];
    };
  }>({
    status: 'idle',
    operation: null,
  });
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = React.useState(false);
  const [shelveDialogOpen, setShelveDialogOpen] = React.useState(false);
  const [progressModalOpen, setProgressModalOpen] = React.useState(false);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const { user } = useAuth();
  const { filters, updateFilters, clearFilters, hasActiveFilters } =
    useMainVorgangFilters();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const state = location.state as VorgaengeAlertPresetLocationState | null;
    if (!state?.presetFilters) {
      return;
    }

    updateFilters(state.presetFilters);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, updateFilters]);

  const effectiveFilters = React.useMemo<VorgangFilters>(
    () => ({
      ...filters,
      ...forcedFilters,
    }),
    [filters, forcedFilters],
  );
  const { exportCsv, isExporting } = useExportMainVorgaengeCsv();

  // Local state for search input (only updates filters on button click)
  const [searchInput, setSearchInput] = React.useState(
    filters.searchText || '',
  );
  const [searchOperator, setSearchOperator] = React.useState<SearchOperator>(
    filters.searchOperator || 'contains',
  );

  // Initialize search input from filters (for page refresh)
  React.useEffect(() => {
    setSearchInput(filters.searchText || '');
  }, [filters.searchText]);

  React.useEffect(() => {
    setSearchOperator(filters.searchOperator || 'contains');
  }, [filters.searchOperator]);

  // Reset to first page when filters or sorting change
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    // Clear selection when filters change significantly

    setSelectedIds(new Set());
  }, [filters, sorting]);

  // Expose current state to parent component
  React.useEffect(() => {
    if (onStateChange) {
      onStateChange({
        filters,
        pagination,
        sorting,
      });
    }
  }, [filters, pagination, sorting, onStateChange]);

  // Fetch data with filters, pagination, and sorting
  const sortArg =
    sorting.length > 0
      ? {
          sortBy: COLUMN_FIELD_MAPPING[sorting[0].id] || sorting[0].id,
          sortOrder: sorting[0].desc ? ('desc' as const) : ('asc' as const),
        }
      : undefined;

  const paginationArg = {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const {
    data: response,
    isLoading,
    error,
  } = useVorgaenge(effectiveFilters, paginationArg, sortArg);
  const data = response?.data || [];
  // Selection handlers (only used when feature flag is enabled)
  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (
      selectedIds.size === data.length &&
      data.every((item) => selectedIds.has(item.id))
    ) {
      // All current page items are selected, deselect all
      setSelectedIds((prev) => {
        const next = new Set(prev);
        data.forEach((item) => next.delete(item.id));
        return next;
      });
    } else {
      // Select all items on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        data.forEach((item) => next.add(item.id));
        return next;
      });
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setProgress({
      status: 'idle',
      operation: null,
    });
    setProgressModalOpen(false);
  };

  const isAllPageSelected =
    data.length > 0 && data.every((item) => selectedIds.has(item.id));
  const isSomePageSelected =
    data.length > 0 && data.some((item) => selectedIds.has(item.id));

  // Bulk operation hooks
  const bulkDeleteMutation = useBulkDeleteVorgaenge();
  const bulkRetryMutation = useBulkRetryVorgaenge();
  const bulkZurueckstelleMutation = useBulkZurueckstellenVorgaenge();

  // Check if any selected items are not in FEHLER status (shelve only allowed for FEHLER)
  const hasNonFehlerItems = React.useMemo(() => {
    return Array.from(selectedIds).some((id) => {
      const item = data.find((d) => d.id === id);
      return item && item.status !== 'FEHLER';
    });
  }, [selectedIds, data]);

  const hasErledigtItems = React.useMemo(() => {
    return Array.from(selectedIds).some((id) => {
      const item = data.find((d) => d.id === id);
      return item && item.status === 'ERFOLGREICH';
    });
  }, [selectedIds, data]);

  // Check if any selected items are locked by other users (only relevant when feature flag is enabled)
  const hasLockedItems = React.useMemo(() => {
    return Array.from(selectedIds).some((id) => {
      const item = data.find((d) => d.id === id);
      return (
        item &&
        item.lockedBy &&
        item.lockedBy !== user?.email &&
        !isVorgangLockExpired(item.lockedAt)
      );
    });
  }, [selectedIds, data, user?.email]);

  // Handle bulk delete confirmation (only used when feature flag is enabled)
  const handleBulkDeleteClick = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleteDialogOpen(true);
  };

  // Handle bulk delete execution (only used when feature flag is enabled)
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleteDialogOpen(false);

    setProgress({
      status: 'processing',
      operation: 'delete',
    });
    setProgressModalOpen(true);

    try {
      const result = await bulkDeleteMutation.mutateAsync({ ids });
      setProgress({
        status: 'completed',
        operation: 'delete',
        result: {
          success: result.success || [],
          failed: result.failed || [],
        },
      });
    } catch {
      setProgress({
        status: 'error',
        operation: 'delete',
      });
    }
  };

  // Handle bulk retry confirmation (only used when feature flag is enabled)
  const handleBulkRetryClick = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setRetryDialogOpen(true);
  };

  // Handle bulk retry execution (only used when feature flag is enabled)
  const handleBulkRetry = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setRetryDialogOpen(false);

    setProgress({
      status: 'processing',
      operation: 'retry',
    });
    setProgressModalOpen(true);

    try {
      const result = await bulkRetryMutation.mutateAsync({ ids });
      setProgress({
        status: 'completed',
        operation: 'retry',
        result: {
          success: result.success || [],
          failed: result.failed || [],
        },
      });
    } catch {
      setProgress({
        status: 'error',
        operation: 'retry',
      });
    }
  };

  // Handle bulk shelve confirmation
  const handleBulkShelveClick = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setShelveDialogOpen(true);
  };

  // Handle bulk shelve execution
  const handleBulkShelve = async (kommentar: string) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setShelveDialogOpen(false);

    setProgress({
      status: 'processing',
      operation: 'shelve',
    });
    setProgressModalOpen(true);

    try {
      const result = await bulkZurueckstelleMutation.mutateAsync({
        ids,
        kommentar,
      });
      setProgress({
        status: 'completed',
        operation: 'shelve',
        result: {
          success: result.success || [],
          failed: result.failed || [],
        },
      });
    } catch {
      setProgress({
        status: 'error',
        operation: 'shelve',
      });
    }
  };

  const columns = getColumns({
    filters,
    updateFilters,
    isAllPageSelected: isAllPageSelected,
    isSomePageSelected: isSomePageSelected,
    toggleSelectAll,
    toggleSelection,
    selectedIds,
    onEdit,
    selectable: true,
    user,
    statusOptions: MAIN_STATUS_FILTER_OPTIONS,
    showCurrentStep: true,
  });

  const buildExportRequest = ({ exportAll }: { exportAll: boolean }) => {
    const sortBy =
      sorting.length > 0
        ? COLUMN_FIELD_MAPPING[sorting[0].id] || sorting[0].id
        : undefined;
    const sortOrder = sorting[0]?.desc ? ('desc' as const) : ('asc' as const);

    return {
      filters: effectiveFilters,
      pagination: {
        page: exportAll ? 1 : pagination.pageIndex + 1,
        pageSize: exportAll ? 100_000 : pagination.pageSize,
      },
      sorting: sortBy && sortOrder ? { sortBy, sortOrder } : undefined,
      columns: [
        'id',
        'externerName',
        'currentStatus',
        'erstelltDatum',
        'currentGestartetAm',
        'auftragsnummer',
        'kundennummer',
        'vertragsnummer',
        'errorMessage',
        'errorStep',
        'lockedBy',
        'dokumentTyp',
        'unterdrueckung',
      ],
    };
  };

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(fullColumnOrder) : updater;
      // Keep pinned columns out of the persisted user order.
      setColumnOrder(next.filter((id) => columnOrder.includes(id)));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: '-',
    manualPagination: true,
    manualSorting: true, // Enable server-side sorting
    pageCount: -1, // Unknown total pages
    state: {
      sorting,
      rowSelection,
      pagination,
      columnVisibility,
      columnOrder: fullColumnOrder,
    },
  });

  // Handle individual filter removal
  const handleRemoveFilter = (key: string) => {
    if (key === 'createdAt' || key === 'currentGestartedAm') {
      updateFilters({
        [key]: { from: undefined, to: undefined },
      });
    } else if (key === 'searchText') {
      setSearchInput('');
      setSearchOperator('contains');
      updateFilters({ searchText: '', searchOperator: 'contains' });
    } else if (key === 'stale24h') {
      updateFilters({ stale24h: false });
    } else {
      updateFilters({ [key]: '' });
    }
  };

  // Handle search functionality
  const handleSearch = () => {
    updateFilters({ searchText: searchInput, searchOperator });
  };

  const handleOperatorChange = (operator: SearchOperator) => {
    setSearchOperator(operator);
    if (searchInput.trim()) {
      updateFilters({ searchText: searchInput, searchOperator: operator });
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchOperator('contains');
    updateFilters({ searchText: '', searchOperator: 'contains' });
  };

  return (
    <div className="flex flex-col space-y-4 h-[calc(100vh-12rem)]">
      {/* Search & Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        {/* Search Bar */}
        <div
          className={`${
            !isLoading && !error && showBulkActions && selectedIds.size > 0
              ? 'flex'
              : 'lg:col-span-2'
          }`}
        >
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            operator={searchOperator}
            onOperatorChange={handleOperatorChange}
          />
        </div>

        {/* Bulk Selection Bar */}
        {!isLoading && !error && showBulkActions && selectedIds.size > 0 && (
          <BulkSelectionActions
            selectedCount={selectedIds.size}
            hasLockedItems={hasLockedItems}
            hasNonFehlerItems={hasNonFehlerItems}
            hasErledigtItems={hasErledigtItems}
            status={progress.status}
            isBulkDeletePending={bulkDeleteMutation.isPending}
            isBulkRetryPending={bulkRetryMutation.isPending}
            onClearSelection={clearSelection}
            onBulkRetryClick={handleBulkRetryClick}
            onBulkDeleteClick={handleBulkDeleteClick}
            onBulkShelveClick={handleBulkShelveClick}
          />
        )}
      </div>

      <FilterSummary
        filters={filters}
        hasActiveFilters={hasActiveFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAllFilters={clearFilters}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <ToniTaube width={300} height={300} />
        </div>
      ) : error ? (
        <div className="bg-white border border-table-border rounded-lg overflow-hidden p-8 text-center">
          <div className="text-red-600">Fehler beim Laden der Vorgänge</div>
          {hasActiveFilters && (
            <div className="mt-4 text-sm text-gray-600">
              Möglicherweise sind die aktiven Filter die Ursache für den Fehler.
              Versuchen Sie, die Filter zu löschen.
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Bulk Delete Confirmation Dialog */}
          <DeleteDialog
            isBulkOperation
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            selectedCount={selectedIds.size}
            onDelete={handleBulkDelete}
          />

          {/* Bulk Retry Confirmation Dialog */}
          <BulkRetryConfirmationDialog
            open={retryDialogOpen}
            onOpenChange={setRetryDialogOpen}
            selectedCount={selectedIds.size}
            handleBulkRetry={handleBulkRetry}
          />

          {/* Bulk Shelve Confirmation Dialog */}
          <ZurueckstellenDialog
            isBulkOperation
            open={shelveDialogOpen}
            onOpenChange={setShelveDialogOpen}
            selectedCount={selectedIds.size}
            onZurueckstellen={handleBulkShelve}
          />

          {/* Bulk Operation Progress Modal */}
          {progress.operation && (
            <BulkOperationProgressModal
              open={progressModalOpen}
              onClose={() => {
                if (progress.status !== 'processing') {
                  clearSelection();
                }
              }}
              operation={progress.operation}
              progress={{
                status:
                  progress.status === 'idle' ? 'processing' : progress.status,
              }}
              result={progress.result}
              selectedCount={selectedIds.size}
            />
          )}

          {/* Table */}
          <div className={VorgangTableClassNames.shell}>
            <VorgangTableScrollContainer>
              <table className="w-full">
                {/* Table Header */}
                <thead className={VorgangTableClassNames.header}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            VorgangTableClassNames.headerCell,
                            header.id === 'select' && 'w-12',
                          )}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`flex items-center justify-start ${
                                header.column.getCanSort()
                                  ? 'cursor-pointer select-none'
                                  : ''
                              }`}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <div className="flex items-center">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </div>
                              {header.column.getCanSort() && (
                                <div className="ml-1 flex items-center">
                                  {getSortIcon(header.column.getIsSorted())}
                                </div>
                              )}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                {/* Table Body */}
                <tbody>
                  {table.getRowModel().rows.length === 0 && hasActiveFilters ? (
                    <tr>
                      <td
                        colSpan={table.getVisibleLeafColumns().length}
                        className="p-8 text-center"
                      >
                        <div className="text-gray-600 text-lg font-medium">
                          Keine Einträge mit den gewählten Filtern gefunden
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          Versuchen Sie, die Filter anzupassen oder zu löschen,
                          um mehr Ergebnisse zu sehen.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row, index) => {
                      const isFirstRow = index === 0;
                      const isLastRow =
                        index === table.getRowModel().rows.length - 1;
                      const isLocked =
                        !!row.original.lockedBy &&
                        !isVorgangLockExpired(row.original.lockedAt);
                      const isSelected = selectedIds.has(row.original.id);
                      return (
                        <tr
                          key={row.id}
                          className={`
                        border-transparent transition-colors
                        border-l-4 hover:border-primary hover:bg-table-hover
                        ${isLocked ? 'opacity-75' : ''}
                        ${isSelected ? 'bg-primary/5 border-l-4 border-l-primary' : ''}
                        ${isFirstRow && isSelected ? 'rounded-tl-lg' : ''}
                        ${isLastRow && isSelected ? 'rounded-bl-lg' : ''}
                      `}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="p-3 text-text-primary font-light text-sm"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </VorgangTableScrollContainer>
          </div>

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            totalPages={null}
            totalCount={data.length}
            onPageChange={(page) => table.setPageIndex(page)}
            onPageSizeChange={(pageSize) => table.setPageSize(pageSize)}
            pageSizeOptions={[25, 50, 100]}
            children={
              <>
                <TableSettingsButton
                  onClick={() => setColumnSettingsOpen(true)}
                />
                <ExportDropDownActions
                  isExporting={isExporting}
                  exportScopeLabel="alle Vorgänge exportieren"
                  onExportCurrentPage={async () =>
                    exportCsv(
                      buildExportRequest({
                        exportAll: false,
                      }),
                    )
                  }
                  onExportAll={async () =>
                    exportCsv(
                      buildExportRequest({
                        exportAll: true,
                      }),
                    )
                  }
                />
              </>
            }
          />

          <TableSettingsModal
            open={columnSettingsOpen}
            onOpenChange={setColumnSettingsOpen}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onColumnOrderChange={setColumnOrder}
            onColumnVisibilityChange={setColumnVisibility}
            onResetToDefault={resetColumnSettings}
          />
        </>
      )}
    </div>
  );
};
